import { useCallback, useEffect, useReducer, useRef } from "react";
import * as Y from "yjs";
import type { Feature, PrimitiveFeature, BooleanFeature, ModifierFeature, ExtrudeFeature, LoftFeature, RevolveFeature, SweepFeature, TransformFeature, SketchFeature, SketchCommand, ImportedPartFeature, MateConstraint, MateType, Vec3 } from "./featureTypes";
import { computeFaceAlignment, computeCoincident, computeRevolute, computeSlider } from "./mateCalculations";
import { isBooleanFeature, hasTargetRef } from "./featureTypes";

/** Compute position + rotation for a given mate constraint. Returns null for types that don't modify transform (fastened). */
function computeMateTransform(
  type: MateType,
  srcPoint: Vec3, srcNormal: Vec3,
  tgtPoint: Vec3, tgtNormal: Vec3,
  angle: number, offset: number,
  fallbackRotation: Vec3,
): { position: Vec3; rotation: Vec3 } | null {
  switch (type) {
    case "faceToFace": return computeFaceAlignment(srcPoint, srcNormal, tgtPoint, tgtNormal);
    case "coincident": return { position: computeCoincident(srcPoint, tgtPoint).position, rotation: fallbackRotation };
    case "revolute": return computeRevolute(srcPoint, srcNormal, tgtPoint, tgtNormal, angle);
    case "slider": return computeSlider(srcPoint, srcNormal, tgtPoint, tgtNormal, offset);
    default: return null; // fastened — no transform change
  }
}
import { yFeatures, ydoc } from "../collab/yjsDoc";
import { generateScript } from "./scriptGenerator";

// ── Helpers ─────────────────────────────────────────────

let counter = 0;
function uid(): string {
  return `f_${Date.now().toString(36)}_${(counter++).toString(36)}`;
}

/** Returns true if refId points to a feature that appears before boolIdx. */
function isValidRef(features: Feature[], refId: string, boolIdx: number): boolean {
  const refIdx = features.findIndex((f) => f.id === refId);
  return refIdx >= 0 && refIdx < boolIdx;
}

/** Returns IDs of all features that reference the given feature ID (booleans + modifiers). */
function getDependents(features: Feature[], featureId: string): string[] {
  return features
    .filter((f) => {
      if (isBooleanFeature(f)) return f.targetRef === featureId || f.toolRef === featureId;
      if (hasTargetRef(f)) return f.targetRef === featureId;
      return false;
    })
    .map((f) => f.id);
}

/** Suffix name with count if there are existing features of the same type. */
function autoName(name: string, type: string, existing: Feature[]): string {
  const count = existing.filter((f) => f.type === type).length;
  return count > 0 ? `${name} ${count + 1}` : name;
}

function defaultFeature(type: PrimitiveFeature["type"]): PrimitiveFeature {
  const base = { id: uid(), enabled: true, position: [0, 0, 0] as Vec3 };
  switch (type) {
    case "box":
      return { ...base, type: "box", name: "Box", width: 20, height: 20, depth: 20 };
    case "cylinder":
      return { ...base, type: "cylinder", name: "Cylinder", radius: 10, height: 20 };
    case "sphere":
      return { ...base, type: "sphere", name: "Sphere", radius: 10 };
  }
}

// ── Reducer ─────────────────────────────────────────────
// Mutations go through Yjs directly; reducer only handles sync + selection.

type Action =
  | { type: "sync"; features: Feature[] }
  | { type: "select"; id: string | null };

interface State {
  features: Feature[];
  selectedId: string | null;
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "sync":
      return { ...state, features: action.features };
    case "select":
      return { ...state, selectedId: action.id };
    default:
      return state;
  }
}

// ── Hook ────────────────────────────────────────────────

export function useFeatureStore(onScriptGenerated: (displayScript: string, execScript: string) => void, docId?: string) {
  const [state, dispatch] = useReducer(reducer, { features: [], selectedId: null });
  const onScriptRef = useRef(onScriptGenerated);
  onScriptRef.current = onScriptGenerated;

  // Sync from Y.Array → local state.
  // Re-subscribe when docId changes (setDocId replaces yFeatures/ydoc).
  // Capture the current yFeatures ref so cleanup unobserves the correct array.
  useEffect(() => {
    const currentFeatures = yFeatures;
    const syncFromY = () => {
      dispatch({ type: "sync", features: currentFeatures.toArray() as Feature[] });
    };

    currentFeatures.observe(syncFromY);
    syncFromY(); // initial

    return () => {
      currentFeatures.unobserve(syncFromY);
    };
  }, [docId]);

  // Undo manager — tracks yFeatures mutations for Cmd+Z / Cmd+Shift+Z
  const undoManagerRef = useRef<Y.UndoManager | null>(null);
  useEffect(() => {
    const um = new Y.UndoManager(yFeatures, { captureTimeout: 300 });
    undoManagerRef.current = um;
    return () => { um.destroy(); undoManagerRef.current = null; };
  }, [docId]);

  const undo = useCallback(() => { undoManagerRef.current?.undo(); }, []);
  const redo = useCallback(() => { undoManagerRef.current?.redo(); }, []);

  // Generate script whenever features change
  // Display script (for Script tab) + execution script (with __featureShapes for per-feature meshes)
  useEffect(() => {
    const displayScript = generateScript(state.features);
    const execScript = generateScript(state.features, { featureMap: true });
    onScriptRef.current(displayScript, execScript);
  }, [state.features]);

  const internalAddFeature = useCallback((feature: Feature) => {
    ydoc.transact(() => {
      yFeatures.push([feature]);
    });
    dispatch({ type: "select", id: feature.id });
  }, []);

  const addBooleanFeature = useCallback((boolType: BooleanFeature["type"]) => {
    const arr = yFeatures.toArray() as Feature[];
    if (arr.length < 2) return;
    const baseName = boolType.charAt(0).toUpperCase() + boolType.slice(1);
    internalAddFeature({
      id: uid(),
      name: autoName(baseName, boolType, arr),
      type: boolType,
      enabled: true,
      targetRef: arr[0].id,
      toolRef: arr[1].id,
    } as BooleanFeature);
  }, [internalAddFeature]);

  const addModifierFeature = useCallback((modType: ModifierFeature["type"]) => {
    const arr = yFeatures.toArray() as Feature[];
    if (arr.length < 1) return;
    const baseName = modType.charAt(0).toUpperCase() + modType.slice(1);
    const defaults: Record<string, Record<string, unknown>> = {
      fillet: { radius: 2 },
      chamfer: { distance: 2 },
      shell: { thickness: 1 },
      draft: { angle: 5, axis: "Z" },
    };
    internalAddFeature({
      id: uid(),
      name: autoName(baseName, modType, arr),
      type: modType,
      enabled: true,
      targetRef: arr[arr.length - 1].id,
      ...defaults[modType],
    } as ModifierFeature);
  }, [internalAddFeature]);

  const addFeature = useCallback((featureType: PrimitiveFeature["type"]) => {
    const f = defaultFeature(featureType);
    f.name = autoName(f.name, featureType, yFeatures.toArray() as Feature[]);
    internalAddFeature(f);
  }, [internalAddFeature]);

  const addExtrudeFeature = useCallback((profile: ExtrudeFeature["profile"]) => {
    const arr = yFeatures.toArray() as Feature[];
    internalAddFeature({
      id: uid(),
      name: autoName("Extrude", "extrude", arr),
      type: "extrude",
      enabled: true,
      profile,
      profileWidth: profile === "circle" ? undefined : 20,
      profileHeight: profile === "circle" ? undefined : 20,
      profileRadius: profile === "circle" ? 10 : profile === "rounded-rectangle" ? 3 : undefined,
      depth: 10,
      position: [0, 0, 0],
    } as ExtrudeFeature);
  }, [internalAddFeature]);

  const addSketchFeature = useCallback(() => {
    const arr = yFeatures.toArray() as Feature[];
    internalAddFeature({
      id: uid(),
      name: autoName("Sketch", "sketch", arr),
      type: "sketch",
      enabled: true,
      commands: [
        { tool: "moveTo", point: [-10, -10] },
        { tool: "lineTo", point: [10, -10] },
        { tool: "lineTo", point: [10, 10] },
        { tool: "lineTo", point: [-10, 10] },
      ],
      plane: "XY",
      closed: true,
      operation: "extrude",
      depth: 10,
      position: [0, 0, 0],
    } as SketchFeature);
  }, [internalAddFeature]);

  const updateSketchCommands = useCallback((id: string, commands: SketchCommand[], closed: boolean) => {
    const arr = yFeatures.toArray() as Feature[];
    const idx = arr.findIndex((f) => f.id === id);
    if (idx >= 0 && arr[idx].type === "sketch") {
      const updated = { ...arr[idx], commands, closed };
      ydoc.transact(() => {
        yFeatures.delete(idx, 1);
        yFeatures.insert(idx, [updated]);
      });
    }
  }, []);

  const applyMate = useCallback((
    mateType: MateConstraint["type"],
    targetFeatureId: string,
    sourcePoint: Vec3, sourceNormal: Vec3,
    targetPoint: Vec3, targetNormal: Vec3,
    sourceFeatureId: string,
  ) => {
    const arr = yFeatures.toArray() as Feature[];
    const idx = arr.findIndex((f) => f.id === targetFeatureId);
    if (idx < 0 || arr[idx].type !== "importedPart") return;

    const feature = arr[idx] as ImportedPartFeature;
    const transform = computeMateTransform(
      mateType, sourcePoint, sourceNormal, targetPoint, targetNormal,
      0, 0, feature.importRotation,
    );

    const mate: MateConstraint = {
      id: `mate_${Date.now().toString(36)}`,
      type: mateType,
      sourceFeatureId,
      targetFeatureId,
      sourcePoint, sourceNormal,
      targetPoint, targetNormal,
      constraintAngle: mateType === "revolute" ? 0 : undefined,
      constraintOffset: mateType === "slider" ? 0 : undefined,
    };

    const updated: ImportedPartFeature = {
      ...feature,
      importPosition: transform?.position ?? feature.importPosition,
      importRotation: transform?.rotation ?? feature.importRotation,
      mates: [...(feature.mates ?? []), mate],
    };

    ydoc.transact(() => {
      yFeatures.delete(idx, 1);
      yFeatures.insert(idx, [updated]);
    });
  }, []);

  const removeMate = useCallback((featureId: string, mateId: string) => {
    const arr = yFeatures.toArray() as Feature[];
    const idx = arr.findIndex((f) => f.id === featureId);
    if (idx < 0 || arr[idx].type !== "importedPart") return;
    const feature = arr[idx] as ImportedPartFeature;
    const updated: ImportedPartFeature = {
      ...feature,
      mates: (feature.mates ?? []).filter((m) => m.id !== mateId),
    };
    ydoc.transact(() => {
      yFeatures.delete(idx, 1);
      yFeatures.insert(idx, [updated]);
    });
  }, []);

  const updateMate = useCallback((featureId: string, mateId: string, changes: Partial<MateConstraint>) => {
    const arr = yFeatures.toArray() as Feature[];
    const idx = arr.findIndex((f) => f.id === featureId);
    if (idx < 0 || arr[idx].type !== "importedPart") return;
    const feature = arr[idx] as ImportedPartFeature;
    if (!feature.mates) return;
    const mateIdx = feature.mates.findIndex((m) => m.id === mateId);
    if (mateIdx < 0) return;

    const mate = { ...feature.mates[mateIdx], ...changes };
    const transform = computeMateTransform(
      mate.type, mate.sourcePoint, mate.sourceNormal,
      mate.targetPoint, mate.targetNormal,
      mate.constraintAngle ?? 0, mate.constraintOffset ?? 0,
      feature.importRotation,
    );

    const mates = [...feature.mates];
    mates[mateIdx] = mate;
    const updated: ImportedPartFeature = {
      ...feature,
      importPosition: transform?.position ?? feature.importPosition,
      importRotation: transform?.rotation ?? feature.importRotation,
      mates,
    };
    ydoc.transact(() => {
      yFeatures.delete(idx, 1);
      yFeatures.insert(idx, [updated]);
    });
  }, []);

  const replaceImportedPart = useCallback((featureId: string, newDocId: string, newDocName: string, newScript?: string, config?: Record<string, number | string>) => {
    const arr = yFeatures.toArray() as Feature[];
    const idx = arr.findIndex((f) => f.id === featureId);
    if (idx < 0 || arr[idx].type !== "importedPart") return;
    const feature = arr[idx] as ImportedPartFeature;
    const updated: ImportedPartFeature = {
      ...feature,
      importDocId: newDocId,
      importDocName: newDocName,
      importScript: newScript,
      configuration: config,
    };
    ydoc.transact(() => {
      yFeatures.delete(idx, 1);
      yFeatures.insert(idx, [updated]);
    });
  }, []);

  const addImportedPart = useCallback((docId: string, docName: string, script?: string, config?: Record<string, number | string>) => {
    const arr = yFeatures.toArray() as Feature[];
    const f: ImportedPartFeature = {
      id: uid(),
      name: autoName(docName || "Part", "importedPart", arr),
      type: "importedPart",
      enabled: true,
      importDocId: docId,
      importDocName: docName,
      importScript: script,
      importPosition: [0, 0, 0],
      importRotation: [0, 0, 0],
      configuration: config,
    };
    ydoc.transact(() => { yFeatures.push([f]); });
    dispatch({ type: "select", id: f.id });
  }, []);

  const addLoftFeature = useCallback(() => {
    const arr = yFeatures.toArray() as Feature[];
    internalAddFeature({
      id: uid(),
      name: autoName("Loft", "loft", arr),
      type: "loft",
      enabled: true,
      startProfile: "rectangle",
      startWidth: 20, startHeight: 20, startRadius: 10,
      endProfile: "circle",
      endWidth: 10, endHeight: 10, endRadius: 5,
      height: 30,
      ruled: false,
      position: [0, 0, 0],
    } as LoftFeature);
  }, [internalAddFeature]);

  const addRevolveFeature = useCallback((profile: RevolveFeature["profile"]) => {
    const arr = yFeatures.toArray() as Feature[];
    internalAddFeature({
      id: uid(),
      name: autoName("Revolve", "revolve", arr),
      type: "revolve",
      enabled: true,
      profile,
      profileWidth: profile === "circle" ? undefined : 10,
      profileHeight: profile === "circle" ? undefined : 5,
      profileRadius: profile === "circle" ? 3 : profile === "rounded-rectangle" ? 1 : undefined,
      axis: "Z",
      offset: 10,
      angle: 360,
      position: [0, 0, 0],
    } as RevolveFeature);
  }, [internalAddFeature]);

  const addSweepFeature = useCallback(() => {
    const arr = yFeatures.toArray() as Feature[];
    internalAddFeature({
      id: uid(),
      name: autoName("Helix", "sweep", arr),
      type: "sweep",
      enabled: true,
      profileRadius: 1,
      helixRadius: 8,
      pitch: 5,
      turns: 5,
      position: [0, 0, 0],
    } as SweepFeature);
  }, [internalAddFeature]);

  const addTransformFeature = useCallback((tType: TransformFeature["type"]) => {
    const arr = yFeatures.toArray() as Feature[];
    if (arr.length < 1) return;
    const nameMap: Record<string, string> = { mirror: "Mirror", linearPattern: "Linear Pattern", circularPattern: "Circular Pattern" };
    const defaults: Record<string, Record<string, unknown>> = {
      mirror: { plane: "YZ" },
      linearPattern: { axis: "X", count: 3, spacing: 25 },
      circularPattern: { axis: "Z", count: 6, angle: 360 },
    };
    internalAddFeature({
      id: uid(),
      name: autoName(nameMap[tType], tType, arr),
      type: tType,
      enabled: true,
      targetRef: arr[arr.length - 1].id,
      ...defaults[tType],
    } as TransformFeature);
  }, [internalAddFeature]);

  const addSheetMetalBase = useCallback(() => {
    const arr = yFeatures.toArray() as Feature[];
    internalAddFeature({
      id: uid(),
      name: autoName("Sheet", "sheetMetalBase", arr),
      type: "sheetMetalBase" as const,
      enabled: true,
      width: 100,
      length: 60,
      thickness: 1.5,
      bendRadius: 1.5,
      kFactor: 0.44,
      position: [0, 0, 0] as Vec3,
    });
  }, [internalAddFeature]);

  const addSheetMetalFlange = useCallback(() => {
    const arr = yFeatures.toArray() as Feature[];
    const lastBase = [...arr].reverse().find((f) => f.type === "sheetMetalBase" || f.type === "sheetMetalFlange");
    if (!lastBase) return;
    internalAddFeature({
      id: uid(),
      name: autoName("Flange", "sheetMetalFlange", arr),
      type: "sheetMetalFlange" as const,
      enabled: true,
      targetRef: lastBase.id,
      flangeLength: 20,
      flangeAngle: 90,
      edge: "right" as const,
    });
  }, [internalAddFeature]);

  const addSheetMetalCornerRelief = useCallback(() => {
    const arr = yFeatures.toArray() as Feature[];
    const lastSm = [...arr].reverse().find((f) => f.type === "sheetMetalBase" || f.type === "sheetMetalFlange");
    if (!lastSm) return;
    internalAddFeature({
      id: uid(),
      name: autoName("Corner Relief", "sheetMetalCornerRelief", arr),
      type: "sheetMetalCornerRelief" as const,
      enabled: true,
      targetRef: lastSm.id,
      reliefType: "circular" as const,
      reliefScale: 1.0,
    });
  }, [internalAddFeature]);

  const addAssemblyPattern = useCallback((pType: "assemblyLinearPattern" | "assemblyCircularPattern") => {
    const arr = yFeatures.toArray() as Feature[];
    const lastImported = [...arr].reverse().find((f) => f.type === "importedPart");
    if (!lastImported) return;
    const nameMap = { assemblyLinearPattern: "Linear Array", assemblyCircularPattern: "Circular Array" };
    const defaults = {
      assemblyLinearPattern: { axis: "X" as const, count: 4, spacing: 25 },
      assemblyCircularPattern: { axis: "Z" as const, count: 6, angle: 360 },
    };
    internalAddFeature({
      id: uid(),
      name: autoName(nameMap[pType], pType, arr),
      type: pType,
      enabled: true,
      targetRef: lastImported.id,
      ...defaults[pType],
    } as any);
  }, [internalAddFeature]);

  const deleteFeature = useCallback((id: string) => {
    ydoc.transact(() => {
      const arr = yFeatures.toArray() as Feature[];
      // Cascade: collect this feature + all dependents (booleans + modifiers) recursively
      const toDelete = new Set<string>();
      const queue = [id];
      while (queue.length > 0) {
        const current = queue.pop()!;
        if (toDelete.has(current)) continue;
        toDelete.add(current);
        for (const depId of getDependents(arr, current)) {
          queue.push(depId);
        }
      }
      // Delete from highest index to lowest to preserve indices
      const indices = [...toDelete]
        .map((delId) => arr.findIndex((f) => f.id === delId))
        .filter((i) => i >= 0)
        .sort((a, b) => b - a);
      for (const idx of indices) {
        yFeatures.delete(idx, 1);
      }
    });
    dispatch({ type: "select", id: null });
  }, []);

  const toggleFeature = useCallback((id: string) => {
    const arr = yFeatures.toArray() as Feature[];
    const idx = arr.findIndex((f) => f.id === id);
    if (idx >= 0) {
      const updated = { ...arr[idx], enabled: !arr[idx].enabled };
      ydoc.transact(() => {
        yFeatures.delete(idx, 1);
        yFeatures.insert(idx, [updated]);
      });
    }
  }, []);

  const updateFeature = useCallback((id: string, changes: Partial<Feature>) => {
    const arr = yFeatures.toArray() as Feature[];
    const idx = arr.findIndex((f) => f.id === id);
    if (idx >= 0) {
      const updated = { ...arr[idx], ...changes };
      // Validate refs if changed
      if ("targetRef" in changes && !isValidRef(arr, (updated as any).targetRef, idx)) {
        return;
      }
      if ("toolRef" in changes && !isValidRef(arr, (updated as any).toolRef, idx)) {
        return;
      }
      ydoc.transact(() => {
        yFeatures.delete(idx, 1);
        yFeatures.insert(idx, [updated]);
      });
    }
  }, []);

  const moveFeature = useCallback((id: string, direction: "up" | "down") => {
    const arr = yFeatures.toArray() as Feature[];
    const idx = arr.findIndex((f) => f.id === id);
    const newIdx = direction === "up" ? idx - 1 : idx + 1;
    if (idx < 0 || newIdx < 0 || newIdx >= arr.length) return;

    // Validate: swapping must not break ref constraints.
    // A feature with refs (targetRef/toolRef) can't move before its references.
    // A feature that IS referenced can't move after its dependents.
    const moving = arr[idx];
    const swapping = arr[newIdx];

    if (direction === "up") {
      // Moving `moving` before `swapping`. Check: does `moving` reference `swapping`?
      if (hasTargetRef(moving) && moving.targetRef === swapping.id) return;
      if (isBooleanFeature(moving) && moving.toolRef === swapping.id) return;
    } else {
      // Moving `moving` after `swapping`. Check: does `swapping` reference `moving`?
      if (hasTargetRef(swapping) && swapping.targetRef === moving.id) return;
      if (isBooleanFeature(swapping) && swapping.toolRef === moving.id) return;
    }

    ydoc.transact(() => {
      // Remove from old position, insert at new position
      yFeatures.delete(idx, 1);
      yFeatures.insert(newIdx, [moving]);
    });
  }, []);

  const selectFeature = useCallback((id: string | null) => {
    dispatch({ type: "select", id });
  }, []);

  // Keyboard shortcuts: Cmd+Z undo, Cmd+Shift+Z redo, Delete/Backspace delete selected
  const selectedIdRef = useRef(state.selectedId);
  selectedIdRef.current = state.selectedId;
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

      if ((e.metaKey || e.ctrlKey) && e.key === "z") {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      } else if ((e.key === "Delete" || e.key === "Backspace") && selectedIdRef.current) {
        e.preventDefault();
        deleteFeature(selectedIdRef.current);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [undo, redo, deleteFeature]);

  const selectedFeature = state.features.find((f) => f.id === state.selectedId) ?? null;

  return {
    features: state.features,
    selectedId: state.selectedId,
    selectedFeature,
    addFeature,
    addExtrudeFeature,
    addSketchFeature,
    updateSketchCommands,
    addImportedPart,
    applyMate,
    removeMate,
    updateMate,
    replaceImportedPart,
    addLoftFeature,
    addRevolveFeature,
    addSweepFeature,
    addBooleanFeature,
    addModifierFeature,
    addTransformFeature,
    addAssemblyPattern,
    addSheetMetalBase,
    addSheetMetalFlange,
    addSheetMetalCornerRelief,
    deleteFeature,
    toggleFeature,
    updateFeature,
    moveFeature,
    selectFeature,
    undo,
    redo,
  };
}
